import * as React from 'react'
import { classnames } from '@halo/_utils/classnames'
import { PageHeader } from '@pushly/aqe/lib/components'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { UserDataForm } from '@halo/components/user-data-form/user-data-form'
import querystring from 'query-string'
import './style/user.scss'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { ORGANIZATIONS_ROUTE } from '@halo/portals/post-auth/accounts'
import { USERS_ROUTE } from '@halo/portals/post-auth/users'
import { Loading3QuartersOutlined } from '@ant-design/icons'
import { Breadcrumb } from 'antd'
import { UserDto } from '@halo/dtos/user.dto'
import { AccountDto } from '@halo/dtos/account.dto'
import { UserService } from '@halo/services/user.service'
import { AccountService } from '@halo/services/account.service'
import { RouteComponentProps } from 'react-router-dom'

interface IProps
    extends RouteComponentProps<
        { accountId?: string; userId: string },
        any,
        { user?: UserDto; account?: AccountDto }
    > {}

interface IUserState {
    loading: boolean
    user?: UserDto
    account?: AccountDto
}

export class User extends React.Component<IProps, IUserState> {
    protected appState: AppState
    protected appService: AppService
    protected userService: UserService
    protected accountService: AccountService

    public constructor(props: IProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.userService = Container.get(UserService)
        this.accountService = Container.get(AccountService)

        this.state = {
            loading: !props.location.state?.user || !props.location.state?.account,
            user: props.location.state?.user,
            account: props.location.state?.account,
        }
    }

    public componentDidMount() {
        if (!this.state.user || !this.state.account) {
            this.loadDependencies()
        }
    }

    public render() {
        return (
            <div className={classnames('page', 'single-user')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader
                        title="User"
                        onTitleSet={noop}
                        append={
                            this.state.loading ? (
                                <Loading3QuartersOutlined spin={this.state.loading} />
                            ) : (
                                <Breadcrumb>
                                    {!!this.state.account && (
                                        <React.Fragment>
                                            <Breadcrumb.Item
                                                href={ORGANIZATIONS_ROUTE}
                                                onClick={this.handleBreadcrumbClick}
                                            >
                                                Organizations
                                            </Breadcrumb.Item>
                                            <Breadcrumb.Item
                                                href={`${ORGANIZATIONS_ROUTE}/${this.state.account.id}`}
                                                onClick={this.handleBreadcrumbClick}
                                            >
                                                {this.state.account.name}
                                            </Breadcrumb.Item>
                                        </React.Fragment>
                                    )}
                                    <Breadcrumb.Item
                                        href={
                                            this.state.account
                                                ? `${ORGANIZATIONS_ROUTE}/${this.state.account.id}#users`
                                                : USERS_ROUTE
                                        }
                                        onClick={this.handleBreadcrumbClick}
                                    >
                                        Users
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item>{this.state.user.name}</Breadcrumb.Item>
                                </Breadcrumb>
                            )
                        }
                    />

                    <UserDataForm userId={this.requestedUserId} accountId={this.requestedAccountIdFilter} />
                </div>
            </div>
        )
    }

    protected get requestedUserId(): number {
        return parseInt(this.props.match.params.userId, 10)
    }

    protected get requestedAccountIdPathParam(): number {
        return parseInt(this.props.match.params.accountId, 10)
    }

    protected get requestedAccountIdFilter(): number {
        const qs = querystring.parse(this.props.location.search)
        let accountId: any = this.props.match.params.accountId ?? qs.account_id ?? qs.accountId
        if (accountId) {
            accountId = parseInt(accountId, 10)
        }
        return accountId
    }

    protected handleBreadcrumbClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
        if (!ev.metaKey) {
            ev.preventDefault()
            this.appService.route(ev.currentTarget.getAttribute('href'), {
                account: this.props.location.state?.account,
            })
        }
    }

    protected async loadDependencies() {
        this.setState({ loading: true })

        const loaders: Array<Promise<unknown>> = []

        if (!this.state.account) {
            loaders.push(this.loadRequestedAccount())
        }
        if (!this.state.user) {
            loaders.push(this.loadRequestedUser())
        }

        await Promise.all(loaders)

        this.setState({ loading: false })
    }

    protected async loadRequestedUser() {
        try {
            const { ok, data } = await this.userService.fetchById(this.requestedUserId)
            if (ok) {
                this.setState({ user: data })
            }
        } catch {}
    }

    protected async loadRequestedAccount() {
        if (this.requestedAccountIdFilter) {
            try {
                const account = await this.accountService.fetchById(this.requestedAccountIdFilter)
                if (account) {
                    this.setState({ account })
                }
            } catch {}
        }
    }
}
